import { useState, useEffect, useCallback, useMemo } from 'react'
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  BugAntIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { api, authService, firebaseLogout, useAuthStore } from '@lipaflow/shared'
import { Logo, DarkModeToggle, useDrawerFocus } from '@lipaflow/ui'
import { adminService } from '../services/admin'
import { PageTitleProvider } from '../lib/PageTitleProvider'
import { usePageTitleContext } from '../lib/pageTitle'
import { PLATFORM_PAGES, SECTOR_PAGES } from '../sector/pages'
import { lastSectorCode, sectorPath, useSectors, type Sector } from '../sector/sector'
import { SectorPicker } from './SectorPicker'

type NavItem = { name: string; href: string; icon: React.ElementType }
type NavGroup = { heading?: string; items: NavItem[] }

/** The workspace the address is in: a sector, or the platform. On a platform
 * page, `sector` is the one last worked in (for the way back). */
function useWorkspace(): { sector: Sector | null; sectors: Sector[]; onPlatform: boolean } {
  const { pathname } = useLocation()
  const { data: sectors = [] } = useSectors()
  const [, first] = pathname.split('/')
  const inAddress = sectors.find((s) => s.code === first)
  const last = sectors.find((s) => s.code === lastSectorCode())
  return { sector: inAddress ?? last ?? sectors[0] ?? null, sectors, onPlatform: first === 'platform' }
}

// Grouped by the job an operator is doing. "Tenant" is platform vocabulary
// that collides with renters in the property product (brand doc §6), so a
// customer organisation is a "customer" everywhere a person reads it.
// One workspace at a time: a sector's pages, or the platform's.
function navGroups(sector: Sector | null, onPlatform: boolean): NavGroup[] {
  if (onPlatform || !sector) {
    return [{ items: PLATFORM_PAGES.map((page) => ({ name: page.name, href: `/platform/${page.path}`, icon: page.icon })) }]
  }
  const groups: NavGroup[] = []
  for (const page of SECTOR_PAGES) {
    if (page.sectors && !page.sectors.includes(sector.code)) continue
    const heading = page.group ?? undefined
    const last = groups[groups.length - 1]
    const item = { name: page.name, href: sectorPath(sector.code, page.path), icon: page.icon }
    if (last && last.heading === heading) last.items.push(item)
    else groups.push({ heading, items: [item] })
  }
  return groups
}

function NavGroups({ onNavigate }: { onNavigate?: () => void }) {
  const { sector, sectors, onPlatform } = useWorkspace()
  return (
    <>
      {sectors.length > 0 && (
        <div className="mb-3">
          <SectorPicker sectors={sectors} current={sector} onPlatform={onPlatform} onNavigate={onNavigate} />
        </div>
      )}
      {navGroups(sector, onPlatform).map((group, index) => (
        <div key={`${group.heading ?? 'top'}-${index}`} className="space-y-0.5">
          {group.heading && (
            <p className="px-3 pt-4 pb-1 font-outfit text-overline uppercase text-ink-faint">{group.heading}</p>
          )}
          {group.items.map((item) => (
            <SidebarLink
              key={item.href}
              to={item.href}
              end={!onPlatform && sector ? item.href === sectorPath(sector.code) : false}
              icon={item.icon}
              label={item.name}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </>
  )
}

export default function AdminLayout() {
  return (
    <PageTitleProvider>
      <AdminLayoutInner />
    </PageTitleProvider>
  )
}

function AdminLayoutInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const drawer = useDrawerFocus<HTMLDivElement>(sidebarOpen, closeSidebar)
  const [impersonating, setImpersonating] = useState(false)
  const [impersonationTenantName, setImpersonationTenantName] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const logout = useAuthStore((state) => state.logout)
  const { sector: workspace, onPlatform } = useWorkspace()

  // Check for impersonation state on mount and when navigating
  useEffect(() => {
    const token = localStorage.getItem('impersonation_token')
    const tenantName = localStorage.getItem('impersonation_tenant_name')
    setImpersonating(!!token)
    setImpersonationTenantName(tenantName || '')
  }, [location.pathname])

  const stopImpersonation = useCallback(async () => {
    try {
      await adminService.stopImpersonation()
    } catch {
      // Continue even if backend call fails
    }
    const originalToken = localStorage.getItem('original_token')
    if (originalToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`
    }
    localStorage.removeItem('impersonation_token')
    localStorage.removeItem('impersonation_tenant_name')
    localStorage.removeItem('original_token')
    setImpersonating(false)
    setImpersonationTenantName('')
    navigate(workspace ? sectorPath(workspace.code, 'customers') : '/')
  }, [navigate, workspace])

  const handleLogout = async () => {
    try {
      await authService.logout()
    } catch {
      // Continue client logout even when backend token revocation fails.
    }
    await firebaseLogout()
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main-content" className="skip-link">Skip to content</a>
      {/* Mobile sidebar overlay */}
      <div
        className={clsx(
          'fixed inset-0 z-50 lg:hidden transition-opacity duration-300',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="fixed inset-0 bg-scrim/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div
          ref={drawer}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className={clsx(
            'fixed inset-y-0 left-0 flex min-h-0 w-72 flex-col bg-surface border-r border-hairline shadow-overlay',
            'transition-transform duration-300 ease-brand',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <SidebarBrand
            actionSlot={
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-ink-faint hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
                aria-label="Close navigation"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          />
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
            <NavGroups onNavigate={() => setSidebarOpen(false)} />
            <a
              href="https://github.com/abith/lipaflow/issues/new?assignees=&labels=bug,staging&template=bug_report.md&title=[Staging] Issue:"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex items-center gap-3 min-h-11 px-3 py-2.5 rounded-button font-inter text-body-sm text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors duration-fast"
            >
              <BugAntIcon className="h-5 w-5" />
              Report issue
            </a>
          </nav>
          <SidebarUserSection onLogout={handleLogout} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow min-h-0 bg-surface border-r border-hairline">
          <SidebarBrand />
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
            <NavGroups />
          </nav>

          <SidebarUserSection onLogout={handleLogout} />
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        <TopBar
          onOpenSidebar={() => setSidebarOpen(true)}
          fallbackPath={location.pathname}
          searchLabel={workspace && !onPlatform ? `Search ${workspace.name} customers…` : null}
          onSearch={(q) => workspace && navigate(`${sectorPath(workspace.code, 'customers')}?q=${encodeURIComponent(q)}`)}
        />

        {/* Impersonation banner */}
        {impersonating && (
          <div className="bg-warning-50 border-b border-warning-200 px-4 sm:px-6 py-2.5">
            <div className="max-w-container mx-auto flex items-center justify-between">
              <p className="font-inter text-body-sm text-warning-800">
                You are impersonating <strong>{impersonationTenantName}</strong>
              </p>
              <button
                onClick={stopImpersonation}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button bg-warning text-white font-inter text-caption font-medium hover:opacity-90 transition-opacity duration-fast"
              >
                <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main id="main-content" tabIndex={-1} className="flex-1 px-4 py-8 sm:px-6">
          <div className="max-w-container mx-auto space-y-6">
            <Outlet />
          </div>
        </main>

        <footer className="px-4 sm:px-6 py-6 border-t border-hairline">
          <div className="max-w-container mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="font-inter text-caption text-ink-faint">
              LipaFlow Admin Console
            </p>
            <p className="font-inter text-caption text-ink-faint">
              Customer operations · Verification · Platform oversight
            </p>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────

function SidebarLink({
  to,
  end,
  icon: Icon,
  label,
  onNavigate,
}: {
  to: string
  end?: boolean
  icon: React.ElementType
  label: string
  onNavigate?: () => void
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-3 min-h-11 px-3 py-2.5 rounded-button font-inter text-body-sm transition-colors duration-fast ease-standard',
          isActive
            ? 'bg-accent-soft text-accent font-medium'
            : 'text-ink-muted hover:bg-surface-alt hover:text-ink'
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full transition-colors duration-fast',
              isActive ? 'bg-accent' : 'bg-transparent group-hover:bg-hairline'
            )}
            aria-hidden
          />
          <Icon className="h-5 w-5 shrink-0" />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  )
}

function SidebarBrand({ actionSlot }: { actionSlot?: React.ReactNode }) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-4 border-b border-hairline">
      <Link to="/" className="flex items-center gap-2 min-w-0">
        <Logo subtitle="Admin Console" size="sm" markStyle="standalone" />
      </Link>
      {actionSlot}
    </div>
  )
}

/** The signed-in person, for the sidebar and the phone header. */
function useMe() {
  const user = useAuthStore((state) => state.user)
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Signed in'
  const initials = [user?.first_name, user?.last_name].map((n) => n?.[0] ?? '').join('').toUpperCase() || name[0]?.toUpperCase() || '?'
  return { name, initials, email: user?.email ?? '' }
}

function SidebarUserSection({ onLogout }: { onLogout: () => void }) {
  const me = useMe()
  return (
    <div className="p-4 border-t border-hairline">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center shrink-0">
          <span className="font-outfit font-semibold text-body-sm text-white">{me.initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-inter text-body-sm font-medium text-ink truncate">{me.name}</p>
          <p className="font-inter text-caption text-ink-muted truncate">{me.email}</p>
        </div>
        <button
          onClick={onLogout}
          className="p-2 text-ink-faint hover:text-danger rounded-button hover:bg-danger-soft transition-colors duration-fast"
          title="Sign out"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

function TopBar({
  onOpenSidebar,
  fallbackPath,
  onSearch,
  searchLabel,
}: {
  onOpenSidebar: () => void
  fallbackPath: string
  onSearch: (q: string) => void
  /** Null on platform pages: customers belong to a sector. */
  searchLabel: string | null
}) {
  const me = useMe()
  const { title } = usePageTitleContext()
  const derivedTitle = useMemo(() => {
    if (title) return title
    const [, first = '', second = '', third] = fallbackPath.split('/')
    if (first === 'platform') return PLATFORM_PAGES.find((p) => p.path === second)?.name ?? 'Platform'
    if (second === 'onboarding' && third) return 'Onboarding: set up a customer'
    return SECTOR_PAGES.find((p) => p.path === second)?.name ?? 'Home'
  }, [title, fallbackPath])

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-hairline bg-surface/80 backdrop-blur px-4 sm:px-6">
      <button
        className="lg:hidden p-2 -ml-2 text-ink-muted hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      <div className="min-w-0 flex-1">
        <h2 className="font-outfit text-h4 text-ink truncate">
          {derivedTitle}
        </h2>
      </div>

      {searchLabel && <form
        className="hidden md:flex items-center gap-2 rounded-button bg-surface-alt px-3 py-2 text-ink-muted"
        onSubmit={(e) => {
          e.preventDefault()
          const input = e.currentTarget.elements.namedItem('search') as HTMLInputElement
          const q = input.value.trim()
          if (q) {
            onSearch(q)
            input.value = ''
          }
        }}
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <input
          name="search"
          type="text"
          placeholder={searchLabel}
          aria-label={searchLabel}
          className="bg-transparent border-none outline-none font-inter text-body-sm text-ink placeholder:text-ink-faint w-56"
        />
      </form>}


      <DarkModeToggle />
      <div className="lg:hidden w-9 h-9 rounded-full bg-clay-500 shadow-sm flex items-center justify-center" aria-hidden>
        <span className="font-outfit font-semibold text-body-sm text-white">{me.initials}</span>
      </div>
    </header>
  )
}
